import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  VStack,
  HStack,
  IconButton,
  useToast,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Badge,
  Select,
  InputGroup,
  InputLeftElement,
  Grid,
  GridItem,
  Wrap,
  WrapItem,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Flex,
  Icon,
  Alert,
  AlertIcon,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Checkbox,
  Textarea,
  Spinner,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon, CopyIcon, CheckIcon, SearchIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { FaUsers, FaFileMedical, FaCheckCircle, FaMapMarkedAlt } from 'react-icons/fa';
import { supabase } from '../../supabaseClient';
import { selectAll } from '../../utils/fetchAllRows';
import { useOutletContext, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import PhlebCoverageMap from './PhlebCoverageMap';
import { getPhlebReadinessStage } from '../../utils/phlebReadiness';

const PhlebotomistManagement = () => {
  const navigate = useNavigate();
  const [phlebotomists, setPhlebotomists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhlebotomist, setSelectedPhlebotomist] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { adminUser } = useOutletContext();
  const [labs, setLabs] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [submissionStats, setSubmissionStats] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilters, setStatusFilters] = useState({
    active: true,
    inactive: true,
    archived: false
  });
  const [adminFilters, setAdminFilters] = useState({
    all: true,
    mine: false,
    unassigned: false
  });
  const [copiedId, setCopiedId] = useState(null);
  const labUpdateProcessed = useRef(false);
  const [newOnboardingLink, setNewOnboardingLink] = useState(null); // { url, name }
  const [setupLink, setSetupLink] = useState(null); // { url, email }

  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    company_name: '',
    company_address: '',
    min_draw_fee: '',
    max_draw_fee: '',
    lab_draw_fee: '',
    phleb_pay_rate: '',
    service_areas: [{ zip_code: '', radius: 10 }],
    send_agreement: 'yes',
    lab_id: '',
  });

  const fetchPhlebotomists = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('phlebotomist_profiles')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPhlebotomists(data || []);
    } catch (error) {
      console.error('Error fetching phlebotomists:', error); // Debug log
      toast({
        title: 'Error fetching phlebotomists',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const fetchLabs = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('labs')
        .select('id, name')
        .order('name', { ascending: true });
      if (error) throw error;
      setLabs(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching labs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  const fetchAdminUsers = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('id, name, role')
        .order('name', { ascending: true });
      if (error) throw error;
      setAdminUsers(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching admin users',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [toast]);

  const fetchSubmissionStats = useCallback(async () => {
    try {
      const { data, error } = await selectAll(() => supabase
        .from('submissions')
        .select('phlebotomist_id, status, submitted_at'));

      if (error) throw error;

      // Group submissions by phlebotomist and count by status
      const stats = {};
      (data || []).forEach(submission => {
        const phlebId = submission.phlebotomist_id;
        const status = submission.status || 'pending';

        if (!stats[phlebId]) {
          stats[phlebId] = {
            total: 0,
            pending: 0,
            in_progress: 0,
            waiting_on_lab_results: 0,
            completed: 0,
            cancelled: 0,
            lastSubmissionAt: null
          };
        }

        stats[phlebId].total++;
        stats[phlebId][status] = (stats[phlebId][status] || 0) + 1;

        // Track most recent submission date
        if (submission.submitted_at) {
          if (!stats[phlebId].lastSubmissionAt || submission.submitted_at > stats[phlebId].lastSubmissionAt) {
            stats[phlebId].lastSubmissionAt = submission.submitted_at;
          }
        }
      });
      
      setSubmissionStats(stats);
    } catch (error) {
      console.error('Error fetching submission stats:', error);
      // Don't show toast for this as it's not critical
    }
  }, []);

  useEffect(() => {
    fetchPhlebotomists();
    fetchLabs();
    fetchAdminUsers();
    fetchSubmissionStats();
  }, [fetchPhlebotomists, fetchLabs, fetchAdminUsers, fetchSubmissionStats]);

  useEffect(() => {
    if (phlebotomists.length > 0 && labs.length > 0 && !labUpdateProcessed.current) {
      const qualityLab = labs.find(l => l.name.toLowerCase().includes('quality laboratory'));
      if (qualityLab) {
        const needsUpdate = phlebotomists.some(phleb => 
          !phleb.lab_id || !labs.some(l => l.id === phleb.lab_id)
        );
        
        if (needsUpdate) {
          labUpdateProcessed.current = true;
          const updates = phlebotomists
            .filter(phleb => !phleb.lab_id || !labs.some(l => l.id === phleb.lab_id))
            .map(phleb => supabase
              .from('phlebotomist_profiles')
              .update({ lab_id: qualityLab.id })
              .eq('id', phleb.id)
            );
          Promise.all(updates).then(() => fetchPhlebotomists());
        }
      }
    }
  }, [phlebotomists, labs, fetchPhlebotomists]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (name, value) => {
    // NumberInput from Chakra UI can return string or number
    // Store it as-is, but we'll parse it properly when saving
    setFormData((prev) => ({
      ...prev,
      [name]: value === '' ? '' : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);


    try {
      // Validate email format before hitting Supabase
      if (!formData.email || !formData.email.trim()) {
        throw new Error('Email address is required.');
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email.trim())) {
        throw new Error('Please enter a valid email address (e.g. name@example.com).');
      }
      if (formData.email.length > 254) {
        throw new Error('Email address is too long. Maximum 254 characters allowed.');
      }

      // New-add flow requires the per-draw fee — it gets locked into the
      // agreement during onboarding and the phleb can't change it later.
      if (!selectedPhlebotomist) {
        const feeNum = parseFloat(formData.lab_draw_fee);
        if (!feeNum || feeNum <= 0) {
          throw new Error('Set a per-draw fee greater than $0. The phleb sees this as read-only during onboarding.');
        }
      }

      // Pre-flight: for brand-new phlebs only, refuse if this email is already
      // attached to another phleb or another role. Without this guard, Supabase's
      // signUp silently no-ops on already-registered emails and the insert
      // downstream fails quietly with an FK error.
      if (!selectedPhlebotomist) {
        const normalizedEmail = formData.email.trim().toLowerCase();

        const { data: existingPhlebByEmail } = await supabase
          .from('phlebotomist_profiles')
          .select('id, full_name, company_name, onboarding_token')
          .ilike('email', normalizedEmail)
          .maybeSingle();

        if (existingPhlebByEmail) {
          // Auto-select the existing phleb so the admin can see/edit them.
          setSelectedPhlebotomist(existingPhlebByEmail);
          throw new Error(
            `A phlebotomist with ${formData.email} already exists` +
            (existingPhlebByEmail.full_name ? ` (${existingPhlebByEmail.full_name})` : '') +
            '. We selected them on the right so you can view or edit.'
          );
        }

        const { data: existingProfile } = await supabase
          .from('profiles')
          .select('id, role, full_name')
          .ilike('email', normalizedEmail)
          .maybeSingle();

        if (existingProfile && existingProfile.role && existingProfile.role !== 'phlebotomist') {
          throw new Error(
            `${formData.email} is already registered as a ${existingProfile.role}. Use a different email for this phlebotomist.`
          );
        }
      }

      // 1. Check if the email exists in profiles
      const { data: existingUser } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', formData.email)
        .single();
      

      let userId;
      let actionType;
      
      // Add created_by_user tracking and auto-assign admin
      const createdByUser = adminUser?.name || 'Unknown Admin';
      
      if (existingUser) {
        // Update the profiles table
        userId = existingUser.id;
        await supabase
          .from('profiles')
          .update({
            full_name: formData.full_name,
            email: formData.email,
            role: 'phlebotomist',
          })
          .eq('id', userId);
        
        // Update or insert the phlebotomist profile
        const { data: phlebProfile } = await supabase
          .from('phlebotomist_profiles')
          .select('id')
          .eq('id', userId)
          .single();
          
        if (phlebProfile) {
          // Update existing phlebotomist profile
          
          // Parse lab_draw_fee properly - handle string, number, empty, or zero
          let labDrawFeeValue = null;
          if (formData.lab_draw_fee !== '' && formData.lab_draw_fee !== null && formData.lab_draw_fee !== undefined) {
            if (typeof formData.lab_draw_fee === 'number') {
              // If it's already a number (including 0), use it directly
              labDrawFeeValue = formData.lab_draw_fee;
            } else if (typeof formData.lab_draw_fee === 'string') {
              const trimmed = formData.lab_draw_fee.trim();
              if (trimmed !== '') {
                const parsed = parseFloat(trimmed);
                // Allow 0 as a valid value
                labDrawFeeValue = isNaN(parsed) ? null : parsed;
              }
            }
          } else if (formData.lab_draw_fee === 0 || formData.lab_draw_fee === '0') {
            // Explicitly handle 0 as a valid value
            labDrawFeeValue = 0;
          }
          
          
          const updateData = {
            full_name: formData.full_name,
            email: formData.email,
            phone: formData.phone,
            company_name: formData.company_name,
            company_address: formData.company_address,
            min_draw_fee: formData.min_draw_fee && formData.min_draw_fee !== '' ? parseFloat(formData.min_draw_fee) : null,
            max_draw_fee: formData.max_draw_fee && formData.max_draw_fee !== '' ? parseFloat(formData.max_draw_fee) : null,
            lab_draw_fee: labDrawFeeValue,
            phleb_pay_rate: formData.phleb_pay_rate && formData.phleb_pay_rate !== '' ? parseFloat(formData.phleb_pay_rate) : null,
            service_areas: formData.service_areas,
            updated_by_user: createdByUser,
          };

          
          // Only add assigned_admin_id if the column exists (after migration)
          // if (assignedAdminId) {
          //   updateData.assigned_admin_id = assignedAdminId;
          // }
          
          const { error: updateError } = await supabase
            .from('phlebotomist_profiles')
            .update(updateData)
            .eq('id', userId);
          
          if (updateError) {
            console.error('Update error:', updateError); // Debug log
            throw updateError;
          }
          actionType = 'Updated Phlebotomist';
        } else {
          // Insert new phlebotomist profile
          
          // Parse lab_draw_fee properly - handle string, number, empty, or zero
          let labDrawFeeValue = null;
          if (formData.lab_draw_fee !== '' && formData.lab_draw_fee !== null && formData.lab_draw_fee !== undefined) {
            if (typeof formData.lab_draw_fee === 'number') {
              labDrawFeeValue = formData.lab_draw_fee;
            } else if (typeof formData.lab_draw_fee === 'string') {
              const trimmed = formData.lab_draw_fee.trim();
              if (trimmed !== '') {
                const parsed = parseFloat(trimmed);
                labDrawFeeValue = isNaN(parsed) ? null : parsed;
              }
            }
          } else if (formData.lab_draw_fee === 0 || formData.lab_draw_fee === '0') {
            labDrawFeeValue = 0;
          }
          
          
          const insertData = {
            id: userId,
            full_name: formData.full_name,
            email: formData.email,
            phone: formData.phone,
            company_name: formData.company_name,
            company_address: formData.company_address,
            min_draw_fee: formData.min_draw_fee && formData.min_draw_fee !== '' ? parseFloat(formData.min_draw_fee) : null,
            max_draw_fee: formData.max_draw_fee && formData.max_draw_fee !== '' ? parseFloat(formData.max_draw_fee) : null,
            lab_draw_fee: labDrawFeeValue,
            phleb_pay_rate: formData.phleb_pay_rate && formData.phleb_pay_rate !== '' ? parseFloat(formData.phleb_pay_rate) : null,
            service_areas: formData.service_areas,
            created_by_user: createdByUser,
          };
          
          
          // Only add assigned_admin_id if the column exists (after migration)
          // if (assignedAdminId) {
          //   insertData.assigned_admin_id = assignedAdminId;
          // }
          
          const { error: insertError } = await supabase
            .from('phlebotomist_profiles')
            .insert([insertData]);
          
          if (insertError) {
            console.error('Insert error:', insertError); // Debug log
            throw insertError;
          }
          actionType = 'Added Phlebotomist';
        }
      } else {
        // 3. Create auth user (needed for profiles FK to auth.users)
        // First try: check if user already exists in phlebotomist_profiles by email
        const { data: existingPhleb } = await supabase
          .from('phlebotomist_profiles')
          .select('id')
          .eq('email', formData.email)
          .single();

        if (existingPhleb) {
          // User exists in phlebotomist_profiles but not in profiles — use their ID
          userId = existingPhleb.id;
          // Ensure profiles entry exists
          await supabase.from('profiles').upsert({
            id: userId,
            full_name: formData.full_name,
            email: formData.email,
            role: 'phlebotomist',
          }, { onConflict: 'id' });
        } else {
          // Create new auth user. If the public signUp rate-limits, fall back to
          // /api/create-phleb-auth-user which uses the service role and skips
          // the confirmation email entirely (no email = no email rate limit).
          const tryAdminFallback = async (reason) => {
            const res = await fetch('/api/create-phleb-auth-user', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                admin_id: adminUser?.id,
                email: formData.email,
                full_name: formData.full_name,
              }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok || !body.user_id) {
              throw new Error(
                `Could not create auth user (${reason}). Server said: ${body.error || `HTTP ${res.status}`}`
              );
            }
            if (body.reused) {
              toast({
                title: 'Linked existing auth user',
                description: 'An auth account with that email already existed and was reused.',
                status: 'info',
                duration: 5000,
                isClosable: true,
              });
            } else {
              toast({
                title: 'Skipped email rate limit',
                description: 'Created the auth user directly via admin API — no confirmation email sent.',
                status: 'info',
                duration: 5000,
                isClosable: true,
              });
            }
            return body.user_id;
          };

          let signUpData;
          let signUpError;
          try {
            const r = await supabase.auth.signUp({
              email: formData.email,
              password: Math.random().toString(36).slice(-12),
              options: {
                data: { role: 'phlebotomist', full_name: formData.full_name },
              },
            });
            signUpData = r.data;
            signUpError = r.error;
          } catch (e) {
            signUpError = e;
          }

          if (signUpError && /rate limit/i.test(signUpError.message || '')) {
            userId = await tryAdminFallback('rate limit');
          } else if (signUpError) {
            throw signUpError;
          } else if (
            !signUpData?.user ||
            (Array.isArray(signUpData.user.identities) && signUpData.user.identities.length === 0)
          ) {
            // Supabase silently returns a fake user when email already registered.
            // Use the admin fallback — it will look up the existing user and reuse it.
            userId = await tryAdminFallback('email already registered');
          } else {
            userId = signUpData.user.id;
          }
        }

        // Ensure profiles entry exists (upsert to handle both new and existing)
        await supabase.from('profiles').upsert({
          id: userId,
          full_name: formData.full_name,
          email: formData.email,
          role: 'phlebotomist',
        }, { onConflict: 'id' });
          
        // Parse lab_draw_fee properly - handle string, number, empty, or zero
        
        let labDrawFeeValue = null;
        if (formData.lab_draw_fee !== '' && formData.lab_draw_fee !== null && formData.lab_draw_fee !== undefined) {
          if (typeof formData.lab_draw_fee === 'number') {
            labDrawFeeValue = formData.lab_draw_fee;
          } else if (typeof formData.lab_draw_fee === 'string') {
            const trimmed = formData.lab_draw_fee.trim();
            if (trimmed !== '') {
              const parsed = parseFloat(trimmed);
              labDrawFeeValue = isNaN(parsed) ? null : parsed;
            }
          }
        } else if (formData.lab_draw_fee === 0 || formData.lab_draw_fee === '0') {
          labDrawFeeValue = 0;
        }
        
        
        // Create the phlebotomist profile
        const newLabData = {
          id: userId,
          full_name: formData.full_name,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.company_name,
          company_address: formData.company_address,
          min_draw_fee: formData.min_draw_fee && formData.min_draw_fee !== '' ? parseFloat(formData.min_draw_fee) : null,
          max_draw_fee: formData.max_draw_fee && formData.max_draw_fee !== '' ? parseFloat(formData.max_draw_fee) : null,
          lab_draw_fee: labDrawFeeValue,
          phleb_pay_rate: formData.phleb_pay_rate && formData.phleb_pay_rate !== '' ? parseFloat(formData.phleb_pay_rate) : null,
          service_areas: formData.service_areas,
          created_by_user: createdByUser,
        };
        
        
        // Only add assigned_admin_id if the column exists (after migration)
        // if (assignedAdminId) {
        //   newLabData.assigned_admin_id = assignedAdminId;
        // }
        
        const { error: profileError } = await supabase
          .from('phlebotomist_profiles')
          .insert([newLabData]);
          
        if (profileError) {
          console.error('Profile creation error:', profileError); // Debug log
          throw profileError;
        }
        actionType = 'Added Phlebotomist';
      }

      // Log to admin_activity_log
      if (adminUser && adminUser.name) {
        await supabase.from('admin_activity_log').insert([
          {
            action: actionType,
            username: adminUser.name,
            details: formData.company_name || formData.full_name || formData.email,
            created_at: new Date().toISOString(),
          },
        ]);
      }

      // Refresh phlebotomists list first
      await fetchPhlebotomists();
      await fetchSubmissionStats();

      // Fetch the newly created/updated phlebotomist to show details
      const { data: newPhlebotomist, error: fetchError } = await supabase
        .from('phlebotomist_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (fetchError) {
        console.error('Error fetching updated phlebotomist:', fetchError);
      }

      if (newPhlebotomist) {
        setSelectedPhlebotomist(newPhlebotomist);

        // Also update it in the phlebotomists array
        setPhlebotomists(prev =>
          prev.map(p => p.id === userId ? newPhlebotomist : p)
        );

        // For brand-new phlebs, surface the onboarding link so admin can send it.
        const wasNew = actionType === 'Added Phlebotomist';
        if (wasNew && newPhlebotomist.onboarding_token) {
          setNewOnboardingLink({
            url: `${window.location.origin}/phlebotomist/onboard/${newPhlebotomist.onboarding_token}`,
            name: newPhlebotomist.full_name || newPhlebotomist.email,
          });
        }
      }

      toast({
        title: 'Success',
        description: 'Phlebotomist saved successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      resetForm();
      onClose();
    } catch (error) {
      console.error('Mobile lab submission error:', error); // Debug log
      const isRateLimit = error.message?.includes('rate limit');
      toast({
        title: 'Error saving phlebotomist',
        description: isRateLimit
          ? 'Email rate limit exceeded. Please wait a few minutes and try again.'
          : error.message,
        status: 'error',
        duration: isRateLimit ? 8000 : 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      company_name: '',
      company_address: '',
      min_draw_fee: '',
      max_draw_fee: '',
      lab_draw_fee: '',
      phleb_pay_rate: '',
      service_areas: [{ zip_code: '', radius: 10 }],
      send_agreement: 'yes',
      lab_id: '',
    });
    setSelectedPhlebotomist(null);
  };

  const addServiceArea = () => {
    setFormData(prev => ({
      ...prev,
      service_areas: [...prev.service_areas, { zip_code: '', radius: 10 }]
    }));
  };

  const removeServiceArea = (index) => {
    setFormData(prev => ({
      ...prev,
      service_areas: prev.service_areas.filter((_, i) => i !== index)
    }));
  };

  const updateServiceArea = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      service_areas: prev.service_areas.map((area, i) => 
        i === index ? { ...area, [field]: value } : area
      )
    }));
  };

  const handleEdit = (phlebotomist) => {
    setSelectedPhlebotomist(phlebotomist);
    setFormData({
      full_name: phlebotomist.full_name || '',
      email: phlebotomist.email || '',
      phone: phlebotomist.phone || '',
      company_name: phlebotomist.company_name || '',
      company_address: phlebotomist.company_address || '',
      min_draw_fee: phlebotomist.min_draw_fee || '',
      max_draw_fee: phlebotomist.max_draw_fee || '',
      lab_draw_fee: phlebotomist.lab_draw_fee || '',
      phleb_pay_rate: phlebotomist.phleb_pay_rate || '',
      service_areas: phlebotomist.service_areas || [{ zip_code: '', radius: 10 }],
      send_agreement: 'yes',
      lab_id: phlebotomist.lab_id || '',
    });
    onOpen();
  };

  const handleArchive = async (id, isCurrentlyArchived = false) => {
    const action = isCurrentlyArchived ? 'restore' : 'archive';
    const confirmMessage = isCurrentlyArchived 
      ? 'Are you sure you want to restore this phlebotomist?' 
      : 'Are you sure you want to archive this phlebotomist? It will be hidden from the main list but can be restored later.';
    
    if (window.confirm(confirmMessage)) {
      try {
        const { error } = await supabase
          .from('phlebotomist_profiles')
          .update({ 
            archived: !isCurrentlyArchived,
            updated_by_user: adminUser?.name || 'Unknown Admin'
          })
          .eq('id', id);

        if (error) throw error;

        // Log the action
        if (adminUser && adminUser.name) {
          const lab = phlebotomists.find(p => p.id === id);
          await supabase.from('admin_activity_log').insert([
            {
              action: isCurrentlyArchived ? 'Restored Phlebotomist' : 'Archived Phlebotomist',
              username: adminUser.name,
              details: lab?.company_name || lab?.full_name || 'Unknown',
              created_at: new Date().toISOString(),
            },
          ]);
        }

        toast({
          title: 'Success',
          description: `Phlebotomist ${action}d successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        fetchPhlebotomists();
      } catch (error) {
        toast({
          title: `Error ${action}ing phlebotomist`,
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  // Default phlebotomist template (fallback if not in localStorage)
  const defaultPhlebotomistTemplate = `This Phlebotomist Service Agreement ("Agreement") is made between At Home Blood Draw Inc ("Company") and the undersigned Phlebotomist / Phlebotomist ("Phlebotomist").

1. Engagement & Scope of Services
Phlebotomist, acting as an independent business, agrees to collect biological specimens from patients pursuant to valid physician orders and to deliver such specimens to the ordering physician's chosen laboratory. The Phlebotomist performs only technical collection services and has no role in ordering, interpreting, or promoting laboratory testing.

2. Compensation
Fixed Fee: Phlebotomist shall receive a fixed per-draw fee of {PER_DRAW_FEE} per specimen collected.
Fair Market Value: This fee reflects fair market value for specimen-collection services and is not based on the volume or value of tests, referrals, or reimbursement.
Payment Timing: Company will remit payment {PAYMENT_FREQUENCY}, one week in arrears, via {PAYMENT_METHOD} to the account designated below.
Pass-Through: Payments represent funds collected from the ordering laboratory and are remitted to Phlebotomist on that laboratory's behalf.

ACH Payment Information
Account Name {BANK_ACCOUNT_NAME}
Bank {BANK_NAME}
Routing # {BANK_ROUTING_NUMBER}
Account # {BANK_ACCOUNT_NUMBER}

3. No Referrals or Marketing
Phlebotomist shall not solicit, influence, or induce any provider, facility, or patient to use any particular laboratory or testing service. Laboratory selection rests solely with the ordering physician or patient.

4. Compliance with Law
Phlebotomist shall comply with all federal and state laws and regulations, including without limitation:
The Anti-Kickback Statute (42 U.S.C. §1320a-7b(b)),
The Stark Law (42 U.S.C. §1395nn),
HIPAA and related privacy/security rules, and
All applicable state laboratory and health-care licensing requirements.

5. No Billing or Double Billing
Phlebotomist shall not bill or seek reimbursement from any patient, insurer, or government health-care program for specimen-collection services compensated under this Agreement unless expressly authorized in writing by the Company.

6. Independent Contractor Status
Phlebotomist operates as an independent contractor, not an employee or agent of Company or any laboratory. Phlebotomist is responsible for all taxes, insurance, licensure, and business expenses.

7. Confidentiality & HIPAA
Phlebotomist may have access to Protected Health Information ("PHI") and shall safeguard such information consistent with HIPAA. A separate Business Associate Agreement (BAA) between the parties will govern PHI handling.

8. Recordkeeping & Audit Rights
Phlebotomist shall maintain accurate records of collections performed and make such records available to Company upon reasonable notice for verification or compliance review.

9. Indemnification
Phlebotomist agrees to indemnify and hold harmless Company from any loss, liability, or expense arising out of Phlebotomist's negligence, misconduct, or violation of applicable law.

10. Insurance
Phlebotomist shall maintain professional liability insurance of not less than $1,000,000 per occurrence / $3,000,000 aggregate and provide proof upon request.

11. Term & Termination
This Agreement begins on {EFFECTIVE_DATE} and continues until terminated by either party with 30 days' written notice. Either party may terminate immediately for cause if the other breaches a material term or loses required licensure.

12. State-Specific Addendum
Phlebotomist acknowledges that additional provisions may be attached for collections performed in certain states to address local regulatory requirements.

13. Entire Agreement
This Agreement, together with any executed BAA and state addenda, constitutes the full understanding between the parties and supersedes all prior discussions.

Attachments
Exhibit A – Business Associate Agreement (for HIPAA)
Exhibit B – Fair Market Value Certification (per-draw rate justification)
Exhibit C – State Addendum (if required)`;

  const getAgreementText = async (phlebotomist) => {
    // Load agreement template from localStorage or use default
    const storedTemplate = localStorage.getItem('phlebotomistAgreementTemplate');
    const template = storedTemplate || defaultPhlebotomistTemplate;
    
    // Try to fetch current agreement from history if available
    let currentAgreement = null;
    let currentPaymentInfo = null;
    
    if (phlebotomist?.current_agreement_id) {
      try {
        const { data: agreementData } = await supabase
          .from('phlebotomist_agreement_history')
          .select('*')
          .eq('id', phlebotomist.current_agreement_id)
          .single();
        if (agreementData) currentAgreement = agreementData;
      } catch (error) {
        console.error('Error fetching agreement history:', error);
      }
    }
    
    if (phlebotomist?.current_payment_info_id) {
      try {
        const { data: paymentData } = await supabase
          .from('phlebotomist_payment_info_history')
          .select('*')
          .eq('id', phlebotomist.current_payment_info_id)
          .single();
        if (paymentData) currentPaymentInfo = paymentData;
      } catch (error) {
        console.error('Error fetching payment info history:', error);
      }
    }
    
    // Get per-draw fee from current agreement history, fallback to legacy field
    const perDrawFee = currentAgreement?.per_draw_fee !== null && currentAgreement?.per_draw_fee !== undefined
      ? `$${parseFloat(currentAgreement.per_draw_fee).toFixed(2)}`
      : (phlebotomist?.lab_draw_fee !== null && phlebotomist?.lab_draw_fee !== undefined
        ? `$${parseFloat(phlebotomist.lab_draw_fee).toFixed(2)}`
        : '$____');
    
    // Get payment frequency and method from current agreement
    const paymentFrequency = currentAgreement?.payment_frequency || 'weekly';
    const paymentMethod = currentAgreement?.payment_method || 'ACH';
    
    // Get bank info from current payment info - use actual values or "Not provided"
    const bankAccountName = currentPaymentInfo?.bank_account_name || 'Not provided';
    const bankName = currentPaymentInfo?.bank_name || 'Not provided';
    const bankRoutingNumber = currentPaymentInfo?.bank_routing_number || 'Not provided';
    // Mask account number - show last 4 digits only
    let bankAccountNumber = 'Not provided';
    if (currentPaymentInfo?.bank_account_number) {
      const accountNum = String(currentPaymentInfo.bank_account_number);
      if (accountNum.length >= 4) {
        bankAccountNumber = '***' + accountNum.slice(-4);
      } else {
        bankAccountNumber = '***' + accountNum; // If shorter than 4 digits, show what we have
      }
    }
    
    
    // Get dates
    const effectiveDate = currentAgreement?.agreement_effective_date
      ? new Date(currentAgreement.agreement_effective_date).toLocaleDateString()
      : (phlebotomist?.agreement_signed_at || phlebotomist?.agreement_date
        ? (phlebotomist.agreement_signed_at ? new Date(phlebotomist.agreement_signed_at).toLocaleDateString() : phlebotomist.agreement_date)
        : new Date().toLocaleDateString());
    
    const signDate = phlebotomist?.agreement_signed_at || phlebotomist?.agreement_date
      ? (phlebotomist.agreement_signed_at ? new Date(phlebotomist.agreement_signed_at).toLocaleDateString() : phlebotomist.agreement_date)
      : new Date().toLocaleDateString();
    
    const printedName = phlebotomist?.agreement_printed_name || phlebotomist?.full_name || 'Phlebotomist';
    const companyName = phlebotomist?.agreement_company_name || phlebotomist?.company_name || 'Company';
    
    // Replace placeholders in template
    const formattedText = template
      .replace(/{PER_DRAW_FEE}/g, perDrawFee)
      .replace(/{COMPENSATION_PER_DRAW}/g, perDrawFee) // Legacy placeholder support
      .replace(/{LAB_DRAW_FEE}/g, perDrawFee) // Legacy placeholder support
      .replace(/{PAYMENT_FREQUENCY}/g, paymentFrequency === 'weekly' ? 'weekly, one week in arrears' : paymentFrequency)
      .replace(/{PAYMENT_METHOD}/g, paymentMethod)
      .replace(/{BANK_ACCOUNT_NAME}/g, bankAccountName)
      .replace(/{BANK_NAME}/g, bankName)
      .replace(/{BANK_ROUTING_NUMBER}/g, bankRoutingNumber)
      .replace(/{BANK_ACCOUNT_NUMBER}/g, bankAccountNumber)
      .replace(/{EFFECTIVE_DATE}/g, effectiveDate)
      .replace(/{SIGNED_DATE}/g, signDate)
      .replace(/{AGREEMENT_DATE}/g, signDate) // Legacy placeholder support
      .replace(/{PRINTED_NAME}/g, printedName)
      .replace(/{PHLEBOTOMIST_NAME}/g, printedName) // Legacy placeholder support
      .replace(/{COMPANY_NAME}/g, companyName)
      .replace(/{SERVICE_AREA}/g, 'Designated service areas'); // Legacy placeholder support
    
    return formattedText;
  };

  const downloadAgreement = async (phlebotomist) => {
    // If phlebotomist doesn't have payment info ID, try to fetch it fresh
    if (!phlebotomist?.current_payment_info_id) {
      try {
        const { data: freshData, error: fetchError } = await supabase
          .from('phlebotomist_profiles')
          .select('id, current_agreement_id, current_payment_info_id')
          .eq('id', phlebotomist.id)
          .single();
        
        if (!fetchError && freshData) {
          phlebotomist.current_agreement_id = freshData.current_agreement_id;
          phlebotomist.current_payment_info_id = freshData.current_payment_info_id;
        }
      } catch (error) {
        console.error('Error fetching fresh phlebotomist data:', error);
      }
    }
    
    const doc = new jsPDF();
    
    // Set document properties
    doc.setProperties({
      title: 'Phlebotomist Agreement',
      subject: 'At Home Blood Draw Inc Partnership Agreement',
      author: 'At Home Blood Draw Inc',
      creator: 'Agreement Management System'
    });

    let yPos = 20;
    const pageWidth = 210; // A4 width in mm
    const margin = 20;
    const contentWidth = pageWidth - (margin * 2);

    // Header
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('At Home Blood Draw Inc', pageWidth / 2, yPos, { align: 'center' });
    yPos += 8;
    
    doc.setFontSize(14);
    doc.setFont('helvetica', 'normal');
    doc.text('Mobile Phlebotomist Service Agreement', pageWidth / 2, yPos, { align: 'center' });
    yPos += 15;

    // Agreement Text
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const agreementText = await getAgreementText(phlebotomist);
    const lines = doc.splitTextToSize(agreementText, contentWidth);
    
    lines.forEach((line) => {
      if (yPos > 250) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(line, margin, yPos);
      yPos += 6;
    });

    // Add new page for signatures if signed
    if (phlebotomist.agreement_signed) {
      doc.addPage();
      yPos = 50;

      // Signatures Section
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('SIGNATURES', pageWidth / 2, yPos, { align: 'center' });
      yPos += 20;

      // Phlebotomist Signature
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('Phlebotomist:', margin, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text(phlebotomist.agreement_printed_name || phlebotomist.full_name || 'Phlebotomist', margin + 40, yPos);
      doc.setFont('helvetica', 'normal');
      yPos += 10;
      
      if (phlebotomist.agreement_signature) {
        try {
          doc.addImage(phlebotomist.agreement_signature, 'PNG', margin, yPos, 60, 20);
          yPos += 25;
        } catch (error) {
          console.error('Error adding signature image:', error);
          doc.line(margin, yPos + 10, margin + 60, yPos + 10);
          yPos += 20;
        }
      } else {
        doc.line(margin, yPos + 10, margin + 60, yPos + 10);
        yPos += 20;
      }
      
      if (phlebotomist.agreement_signed_at || phlebotomist.agreement_date) {
        const signedDate = phlebotomist.agreement_signed_at 
          ? new Date(phlebotomist.agreement_signed_at).toLocaleDateString()
          : phlebotomist.agreement_date;
        doc.text(`Date: ${signedDate}`, margin, yPos);
      }
      
      yPos += 30;
      
      // Company Signature (At Home Blood Draw Inc)
      const companySignature = localStorage.getItem('companySignature');
      const companySignerName = localStorage.getItem('companySignerName') || 'Authorized Representative';
      const companySignerTitle = localStorage.getItem('companySignerTitle') || 'Manager';
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text('At Home Blood Draw Inc:', margin + 90, yPos);
      doc.setFont('helvetica', 'bold');
      doc.text('At Home Blood Draw Inc', margin + 90, yPos + 10);
      yPos += 20;
      
      doc.setFont('helvetica', 'normal');
      doc.text(`By: ${companySignerName}`, margin + 90, yPos);
      yPos += 8;
      doc.text(`Name: ${companySignerName}`, margin + 90, yPos);
      yPos += 8;
      doc.text(`Title: ${companySignerTitle}`, margin + 90, yPos);
      yPos += 15;
      
      // Add company signature image if available
      if (companySignature) {
        try {
          doc.addImage(companySignature, 'PNG', margin + 90, yPos, 60, 20);
          yPos += 25;
        } catch (error) {
          console.error('Error adding company signature to PDF:', error);
          doc.line(margin + 90, yPos + 10, margin + 150, yPos + 10);
          yPos += 20;
        }
      } else {
        console.warn('Company signature not found in localStorage. Please configure it in Admin > Agreement Management.');
        doc.line(margin + 90, yPos, margin + 150, yPos);
        yPos += 20;
      }
      yPos += 8;
      doc.text(`Date: ${new Date().toLocaleDateString()}`, margin + 90, yPos);
    }
    
    // Generate filename
    const entityName = phlebotomist.agreement_printed_name || phlebotomist.full_name || phlebotomist.company_name;
    const filename = `Agreement-${entityName.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
    
    doc.save(filename);
    
    toast({
      title: 'PDF Generated',
      description: 'Agreement PDF has been downloaded',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast({
        title: 'Copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    });
  };

  const getLabName = (lab_id) => {
    const lab = labs.find(l => l.id === lab_id);
    return lab ? lab.name : 'Unknown';
  };

  const toggleStatusFilter = (status) => {
    if (status === 'archived') {
      // When toggling archived, turn off other filters and vice versa
      setStatusFilters(prev => ({
        active: !prev.archived ? false : true,
        inactive: !prev.archived ? false : true,
        archived: !prev.archived
      }));
    } else {
      // When toggling active/inactive, turn off archived
      setStatusFilters(prev => ({
        ...prev,
        [status]: !prev[status],
        archived: false
      }));
    }
  };

  const toggleAdminFilter = (filter) => {
    if (filter === 'all') {
      setAdminFilters({ all: true, mine: false, unassigned: false });
    } else if (filter === 'mine') {
      setAdminFilters({ all: false, mine: true, unassigned: false });
    } else if (filter === 'unassigned') {
      setAdminFilters({ all: false, mine: false, unassigned: true });
    }
  };

  const handleAssignAdmin = useCallback(async (phlebotomistId, adminId) => {
    try {
      const updateData = {
        updated_by_user: adminUser?.name || 'Unknown Admin'
      };
      
      // Only add assigned_admin_id if we're sure the column exists
      // After migration, this will always work
      if (adminId !== undefined) {
        updateData.assigned_admin_id = adminId;
      }
      
      const { error } = await supabase
        .from('phlebotomist_profiles')
        .update(updateData)
        .eq('id', phlebotomistId);

      if (error) {
        // If error contains "column does not exist", show helpful message
        if (error.message.includes('column') && error.message.includes('does not exist')) {
          toast({
            title: 'Database update required',
            description: 'Please run the admin assignment migration script first.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
          return;
        }
        throw error;
      }

      // Update the local state using functional update to avoid dependency on current state
      setPhlebotomists(prevPhlebotomists => 
        prevPhlebotomists.map(p => 
          p.id === phlebotomistId 
            ? { ...p, assigned_admin_id: adminId }
            : p
        )
      );

      // Update selected phlebotomist if it's the one being updated
      setSelectedPhlebotomist(prev => 
        prev?.id === phlebotomistId 
          ? { ...prev, assigned_admin_id: adminId }
          : prev
      );

      const adminName = adminUsers.find(a => a.id === adminId)?.name || 'Unassigned';
      toast({
        title: 'Admin assigned successfully',
        description: `${adminName} has been assigned to this phlebotomist`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error assigning admin',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [adminUser?.name, adminUsers, toast]);

  const getAdminName = (adminId) => {
    const admin = adminUsers.find(a => a.id === adminId);
    return admin ? admin.name : 'Unassigned';
  };

  const getPhlebotomistStats = useCallback((phlebotomistId) => {
    return submissionStats[phlebotomistId] || {
      total: 0,
      pending: 0,
      in_progress: 0,
      waiting_on_lab_results: 0,
      completed: 0,
      cancelled: 0
    };
  }, [submissionStats]);

  const filteredPhlebotomists = useMemo(() => {
    
    const filtered = phlebotomists.filter(phlebotomist => {
      // Search filter
      if (searchQuery) {
        const normalize = (str) => str?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
        
        const matchesSearch = (
          normalize(phlebotomist.full_name).includes(normalize(searchQuery)) ||
          normalize(phlebotomist.company_name).includes(normalize(searchQuery)) ||
          normalize(phlebotomist.email).includes(normalize(searchQuery)) ||
          normalize(phlebotomist.company_address).includes(normalize(searchQuery)) ||
          phlebotomist.phone?.includes(searchQuery)
        );
        if (!matchesSearch) return false;
      }

      // Status filters
      const isActive = phlebotomist.email && phlebotomist.company_name;
      const isArchived = phlebotomist.archived;

      // Always hide archived unless explicitly showing them
      if (isArchived && !statusFilters.archived) return false;
      // If showing archived, only show archived items
      if (statusFilters.archived && !isArchived) return false;

      // Only apply active/inactive filters if not in archived mode AND filters are actually set
      if (!statusFilters.archived && (statusFilters.active || statusFilters.inactive)) {
        // Only filter if the filter is explicitly turned off
        if (!statusFilters.active && isActive && !isArchived) return false;
        if (!statusFilters.inactive && !isActive && !isArchived) return false;
      }

      // Admin filters
      // Check if any phlebotomist has assigned_admin_id (column exists)
      const hasAdminAssignments = phlebotomists.some(p => p.assigned_admin_id !== undefined);
      
      if (hasAdminAssignments) {
        // If column exists, use normal filtering
        if (adminFilters.mine && (!phlebotomist.assigned_admin_id || phlebotomist.assigned_admin_id !== adminUser?.id)) return false;
        if (adminFilters.unassigned && phlebotomist.assigned_admin_id) return false;
      } else {
        // If column doesn't exist, treat "My Phlebotomists" and "Unassigned" as "All"
        // This prevents filtering out everything when database hasn't been migrated yet
        // Only "All Phlebotomists" will work properly until migration is run
      }
      // adminFilters.all shows everything, so no additional filter needed

      return true;
    });

    
    // Sort by most recent submission activity first, then by created_at as fallback
    return filtered.sort((a, b) => {
      const statsA = submissionStats[a.id];
      const statsB = submissionStats[b.id];
      const lastA = statsA?.lastSubmissionAt ? new Date(statsA.lastSubmissionAt) : new Date(0);
      const lastB = statsB?.lastSubmissionAt ? new Date(statsB.lastSubmissionAt) : new Date(0);
      if (lastA.getTime() !== lastB.getTime()) return lastB - lastA;
      // Fallback: created_at descending
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [phlebotomists, searchQuery, statusFilters, adminFilters, adminUser?.id, submissionStats, getPhlebotomistStats]);

  const aggregateStats = useMemo(() => {
    let total = 0, pending = 0, inProgress = 0, waiting = 0, completed = 0, cancelled = 0;
    filteredPhlebotomists.forEach((p) => {
      const s = getPhlebotomistStats(p.id);
      total += s.total;
      pending += s.pending;
      inProgress += s.in_progress;
      waiting += s.waiting_on_lab_results;
      completed += s.completed;
      cancelled += s.cancelled;
    });
    return { total, pending, inProgress, waiting, completed, cancelled };
  }, [filteredPhlebotomists, getPhlebotomistStats]);

  const PhlebotomistCard = ({ phlebotomist, isSelected, onClick }) => {
    const stats = getPhlebotomistStats(phlebotomist.id);
    const isActive = phlebotomist.email && phlebotomist.company_name && !phlebotomist.archived;
    const readiness = getPhlebReadinessStage(phlebotomist);
    return (
      <Box
        p={3}
        borderRadius="lg"
        bg={isSelected ? "blue.50" : "white"}
        border="1px solid"
        borderColor={isSelected ? "blue.300" : "gray.100"}
        cursor="pointer"
        onClick={onClick}
        _hover={{ bg: isSelected ? "blue.50" : "gray.50", borderColor: isSelected ? "blue.300" : "gray.200" }}
        transition="all 0.15s"
        opacity={phlebotomist.archived ? 0.6 : 1}
      >
        <HStack justify="space-between" mb={1}>
          <Text fontWeight="semibold" fontSize="sm" noOfLines={1} flex={1} color="gray.800">
            {phlebotomist.company_name || phlebotomist.full_name}
          </Text>
          <HStack spacing={1} flexShrink={0}>
            {!phlebotomist.archived && (
              <Badge colorScheme={readiness.color} fontSize="2xs" borderRadius="full" title={readiness.description}>
                {readiness.label}
              </Badge>
            )}
            {phlebotomist.archived && <Badge colorScheme="gray" fontSize="2xs" borderRadius="full">Archived</Badge>}
            {!phlebotomist.archived && !isActive && (
              <Badge colorScheme="orange" fontSize="2xs" borderRadius="full">Incomplete</Badge>
            )}
          </HStack>
        </HStack>
        <Text fontSize="xs" color="gray.500" noOfLines={1}>{phlebotomist.full_name}</Text>
        <Text fontSize="xs" color="gray.400" noOfLines={1} mb={2}>{phlebotomist.email}</Text>
        <HStack spacing={2} flexWrap="wrap">
          <Badge variant="subtle" colorScheme="gray" fontSize="2xs">{stats.total} submissions</Badge>
          {stats.pending > 0 && <Badge variant="subtle" colorScheme="yellow" fontSize="2xs">{stats.pending} pending</Badge>}
          {stats.in_progress > 0 && <Badge variant="subtle" colorScheme="blue" fontSize="2xs">{stats.in_progress} active</Badge>}
          {stats.completed > 0 && <Badge variant="subtle" colorScheme="green" fontSize="2xs">{stats.completed} done</Badge>}
        </HStack>
        {phlebotomist.assigned_admin_id && (
          <Text fontSize="2xs" color="purple.500" mt={1}>{getAdminName(phlebotomist.assigned_admin_id)}</Text>
        )}
      </Box>
    );
  };

  const SectionCard = ({ title, children, colorScheme = "gray" }) => (
    <Box w="100%" bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" overflow="hidden">
      <Box px={4} pt={1} pb={3}>
        <Box display="inline-block" bg={`${colorScheme}.50`} px={2} py={0.5} borderRadius="md" mb={3}>
          <Text fontSize="xs" fontWeight="600" color={`${colorScheme}.600`}>{title}</Text>
        </Box>
        {children}
      </Box>
    </Box>
  );

  const DetailField = ({ label, value, mono, color }) => (
    <Box>
      <Text fontSize="2xs" color="gray.400" fontWeight="500" mb={0.5}>{label}</Text>
      <Text fontSize="sm" fontWeight="500" color={color || "gray.800"} fontFamily={mono ? "mono" : "inherit"}>{value || '---'}</Text>
    </Box>
  );

  // Readiness panel: two checkpoints admin marks before a phleb is route-assignable.
  // Click a checkbox to set/unset the timestamp; admin id + optional notes go along.
  const ReadinessPanel = ({ phlebotomist }) => {
    const [suppliesNotes, setSuppliesNotes] = useState(phlebotomist.supplies_notes || '');
    const [routeNotes, setRouteNotes] = useState(phlebotomist.route_ready_notes || '');
    const [saving, setSaving] = useState(null); // 'supplies' | 'route' | null
    // Synchronous guard against rapid double-clicks. `saving` state alone doesn't
    // help — setSaving is async, so two clicks in the same tick both see saving=null
    // and fire parallel writes. The ref flips immediately.
    const savingRef = useRef(false);
    const readiness = getPhlebReadinessStage(phlebotomist);
    const onboarded = phlebotomist.onboarding_status === 'completed' || !!phlebotomist.onboarding_completed_at;

    // Update both `phlebotomists` (list) and `selectedPhlebotomist` (detail view's
    // source row) immutably. Avoids the previous Object.assign on the prop, which
    // could leave sibling components reading mutated data without React knowing.
    const applyUpdate = (id, update) => {
      setPhlebotomists((prev) => prev.map((p) => (p.id === id ? { ...p, ...update } : p)));
      setSelectedPhlebotomist((prev) => (prev && prev.id === id ? { ...prev, ...update } : prev));
    };

    // Toggle the timestamp + admin-id stamp for one of the two checkpoints.
    // When checked → set to now() + current admin. When unchecked → null both.
    const toggle = async (field, currentValue) => {
      // Re-entry guard: if a save is already in flight, ignore the new click.
      if (savingRef.current) return;
      const fieldByKey = field === 'supplies_picked_up_at' ? 'supplies_picked_up_by' : 'route_ready_by';
      const fieldNotesKey = field === 'supplies_picked_up_at' ? 'supplies_notes' : 'route_ready_notes';
      const notesValue = field === 'supplies_picked_up_at' ? suppliesNotes : routeNotes;
      const setKey = field === 'supplies_picked_up_at' ? 'supplies' : 'route';
      savingRef.current = true;
      setSaving(setKey);
      try {
        const update = currentValue
          ? { [field]: null, [fieldByKey]: null }
          : { [field]: new Date().toISOString(), [fieldByKey]: adminUser?.id || null, [fieldNotesKey]: notesValue?.trim() || null };
        const { error } = await supabase
          .from('phlebotomist_profiles')
          .update(update)
          .eq('id', phlebotomist.id);
        if (error) throw error;
        applyUpdate(phlebotomist.id, update);
        toast({
          title: currentValue ? 'Unmarked' : `${field === 'supplies_picked_up_at' ? 'Supplies picked up' : 'Route-ready'} ✓`,
          status: 'success',
          duration: 2000,
        });
      } catch (err) {
        toast({ title: 'Could not update', description: err.message, status: 'error', duration: 4000, isClosable: true });
      } finally {
        savingRef.current = false;
        setSaving(null);
      }
    };

    const saveNotes = async (field) => {
      if (savingRef.current) return;
      const fieldNotesKey = field === 'supplies_picked_up_at' ? 'supplies_notes' : 'route_ready_notes';
      const notesValue = field === 'supplies_picked_up_at' ? suppliesNotes : routeNotes;
      const setKey = field === 'supplies_picked_up_at' ? 'supplies' : 'route';
      savingRef.current = true;
      setSaving(setKey);
      try {
        const update = { [fieldNotesKey]: notesValue?.trim() || null };
        const { error } = await supabase
          .from('phlebotomist_profiles')
          .update(update)
          .eq('id', phlebotomist.id);
        if (error) throw error;
        applyUpdate(phlebotomist.id, update);
        toast({ title: 'Notes saved', status: 'success', duration: 1500 });
      } catch (err) {
        toast({ title: 'Could not save notes', description: err.message, status: 'error', duration: 4000, isClosable: true });
      } finally {
        savingRef.current = false;
        setSaving(null);
      }
    };

    return (
      <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={4}>
        <Flex justify="space-between" align="center" mb={3}>
          <HStack>
            <Box bg={`${readiness.color}.50`} color={`${readiness.color}.600`} p={2} borderRadius="md">
              <Icon as={FaCheckCircle} boxSize={4} />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.500" fontWeight="700" textTransform="uppercase" letterSpacing="0.5px">Readiness</Text>
              <Text fontSize="sm" color="gray.700">{readiness.description}</Text>
            </Box>
          </HStack>
          <Badge colorScheme={readiness.color} px={3} py={1} borderRadius="full" fontSize="xs" fontWeight="700">
            {readiness.label}
          </Badge>
        </Flex>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
          {/* Supplies picked up */}
          <Box p={3} borderRadius="md" bg={phlebotomist.supplies_picked_up_at ? 'orange.50' : 'gray.50'} border="1px solid" borderColor={phlebotomist.supplies_picked_up_at ? 'orange.200' : 'gray.200'}>
            <HStack justify="space-between" mb={2}>
              <Checkbox
                isChecked={!!phlebotomist.supplies_picked_up_at}
                isDisabled={!onboarded || saving === 'supplies'}
                onChange={() => toggle('supplies_picked_up_at', phlebotomist.supplies_picked_up_at)}
                colorScheme="orange"
                size="md"
              >
                <Text fontWeight="700" fontSize="sm">Supplies picked up</Text>
              </Checkbox>
              {saving === 'supplies' && <Spinner size="xs" />}
            </HStack>
            {phlebotomist.supplies_picked_up_at && (
              <Text fontSize="xs" color="gray.500" mb={2}>
                Marked {new Date(phlebotomist.supplies_picked_up_at).toLocaleString()}
                {phlebotomist.supplies_picked_up_by && ` · by ${getAdminName(phlebotomist.supplies_picked_up_by)}`}
              </Text>
            )}
            {!onboarded && (
              <Text fontSize="xs" color="gray.400" mb={2}>Available after onboarding is completed</Text>
            )}
            <Textarea
              value={suppliesNotes}
              onChange={(e) => setSuppliesNotes(e.target.value)}
              onBlur={() => suppliesNotes !== (phlebotomist.supplies_notes || '') && saveNotes('supplies_picked_up_at')}
              placeholder="Notes (optional) — e.g. shipped FedEx 1Z…"
              size="sm"
              rows={2}
              bg="white"
              fontSize="xs"
            />
          </Box>

          {/* Route-ready */}
          <Box p={3} borderRadius="md" bg={phlebotomist.route_ready_at ? 'green.50' : 'gray.50'} border="1px solid" borderColor={phlebotomist.route_ready_at ? 'green.200' : 'gray.200'}>
            <HStack justify="space-between" mb={2}>
              <Checkbox
                isChecked={!!phlebotomist.route_ready_at}
                isDisabled={!phlebotomist.supplies_picked_up_at || saving === 'route'}
                onChange={() => toggle('route_ready_at', phlebotomist.route_ready_at)}
                colorScheme="green"
                size="md"
              >
                <Text fontWeight="700" fontSize="sm">Route-ready</Text>
              </Checkbox>
              {saving === 'route' && <Spinner size="xs" />}
            </HStack>
            {phlebotomist.route_ready_at && (
              <Text fontSize="xs" color="gray.500" mb={2}>
                Marked {new Date(phlebotomist.route_ready_at).toLocaleString()}
                {phlebotomist.route_ready_by && ` · by ${getAdminName(phlebotomist.route_ready_by)}`}
              </Text>
            )}
            {!phlebotomist.supplies_picked_up_at && (
              <Text fontSize="xs" color="gray.400" mb={2}>Available after supplies are picked up</Text>
            )}
            <Textarea
              value={routeNotes}
              onChange={(e) => setRouteNotes(e.target.value)}
              onBlur={() => routeNotes !== (phlebotomist.route_ready_notes || '') && saveNotes('route_ready_at')}
              placeholder="Notes (optional) — e.g. cleared starting Monday"
              size="sm"
              rows={2}
              bg="white"
              fontSize="xs"
            />
          </Box>
        </SimpleGrid>
      </Box>
    );
  };

  const PhlebotomistDetails = ({ phlebotomist }) => {
    const stats = getPhlebotomistStats(phlebotomist.id);
    const [agreementHistory, setAgreementHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [paymentInfo, setPaymentInfo] = useState(null);
    const [showAccountNumber, setShowAccountNumber] = useState(false);
    const [workingHours, setWorkingHours] = useState([]);

    useEffect(() => {
      const fetchAgreementHistory = async () => {
        if (!phlebotomist?.id) return;
        setLoadingHistory(true);
        try {
          const { data, error } = await supabase
            .from('phlebotomist_agreement_history')
            .select('*')
            .eq('phlebotomist_id', phlebotomist.id)
            .order('agreement_signed_at', { ascending: false });

          if (error) throw error;
          setAgreementHistory(data || []);

          if (phlebotomist.current_payment_info_id) {
            const { data: paymentData, error: paymentError } = await supabase
              .from('phlebotomist_payment_info_history')
              .select('*')
              .eq('id', phlebotomist.current_payment_info_id)
              .single();

            if (!paymentError && paymentData) {
              setPaymentInfo(paymentData);
            }
          }

          const { data: hoursData } = await supabase
            .from('working_hours')
            .select('day_of_week, start_time, end_time')
            .eq('phlebotomist_id', phlebotomist.id);
          setWorkingHours(hoursData || []);
        } catch (error) {
          console.error('Error fetching agreement history:', error);
        } finally {
          setLoadingHistory(false);
        }
      };
      fetchAgreementHistory();
    }, [phlebotomist?.id, phlebotomist?.current_payment_info_id]);

    const openCredential = async (path) => {
      if (!path) return;
      try {
        const { data, error } = await supabase.storage
          .from('phleb-credentials')
          .createSignedUrl(path, 300);
        if (error) throw error;
        window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
      } catch (err) {
        toast({ title: 'Could not open file', description: err.message || String(err), status: 'error' });
      }
    };

    const CredentialThumb = ({ label, path, filename, uploadedAt }) => {
      const [thumbUrl, setThumbUrl] = useState(null);
      const isImage = path && /\.(png|jpe?g|webp|gif)$/i.test(path);
      const isPdf = path && /\.pdf$/i.test(path);

      useEffect(() => {
        let cancelled = false;
        if (!path || !isImage) return;
        (async () => {
          const { data, error } = await supabase.storage
            .from('phleb-credentials')
            .createSignedUrl(path, 600);
          if (cancelled || error) return;
          setThumbUrl(data?.signedUrl || null);
        })();
        return () => { cancelled = true; };
      }, [path, isImage]);

      if (!path) return null;
      return (
        <HStack align="start" spacing={3} p={3} bg="gray.50" borderRadius="md">
          <Box
            w="96px" h="96px" borderRadius="md" overflow="hidden" flexShrink={0}
            borderWidth="1px" borderColor="gray.200" bg="white"
            display="flex" alignItems="center" justifyContent="center"
            cursor="pointer" onClick={() => openCredential(path)}
          >
            {isImage && thumbUrl ? (
              <img src={thumbUrl} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            ) : isPdf ? (
              <VStack spacing={0.5}>
                <Text fontSize="xl" color="red.500">PDF</Text>
                <Text fontSize="2xs" color="gray.400">Click to open</Text>
              </VStack>
            ) : (
              <Text fontSize="xs" color="gray.400">File</Text>
            )}
          </Box>
          <Box flex={1} minW={0}>
            <Text fontSize="sm" fontWeight="600" color="gray.700">{label}</Text>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>{filename || path.split('/').pop()}</Text>
            {uploadedAt && (
              <Text fontSize="2xs" color="gray.400">Uploaded {new Date(uploadedAt).toLocaleDateString()}</Text>
            )}
            <Button mt={2} size="xs" variant="outline" onClick={() => openCredential(path)}>
              Open full size
            </Button>
          </Box>
        </HStack>
      );
    };

    const dayLabel = (d) => {
      if (typeof d === 'number') return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d] || String(d);
      const s = String(d || '').toLowerCase();
      return s.charAt(0).toUpperCase() + s.slice(1, 3);
    };
    const dayKey = (d) => (typeof d === 'number' ? d : (
      { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 }[String(d || '').toLowerCase()] ?? 99
    ));
    const sortedHours = [...workingHours].sort((a, b) => dayKey(a.day_of_week) - dayKey(b.day_of_week));
    const fmtTime = (t) => {
      if (!t) return '';
      const m = String(t).match(/^(\d{1,2}):(\d{2})/);
      return m ? `${m[1]}:${m[2]}` : String(t);
    };

    const isActive = phlebotomist.email && phlebotomist.company_name && !phlebotomist.archived;

    return (
      <VStack spacing={4} align="stretch">
        {/* Header Card */}
        <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={4}>
          <Flex justify="space-between" align="center" mb={3}>
            <Box>
              <HStack spacing={3} align="center">
                <Box w="10px" h="10px" borderRadius="full" bg={isActive ? "green.400" : phlebotomist.archived ? "gray.400" : "orange.400"} />
                <Text fontSize="lg" fontWeight="700" color="gray.800">{phlebotomist.company_name || phlebotomist.full_name}</Text>
                <Badge colorScheme={isActive ? "green" : phlebotomist.archived ? "gray" : "orange"} fontSize="2xs" borderRadius="full">
                  {phlebotomist.archived ? 'Archived' : isActive ? 'Active' : 'Incomplete'}
                </Badge>
              </HStack>
              {phlebotomist.company_name && <Text fontSize="sm" color="gray.500" ml="22px">{phlebotomist.full_name}</Text>}
            </Box>
            <HStack spacing={2}>
              <Button size="sm" variant="outline" leftIcon={<EditIcon />} onClick={() => handleEdit(phlebotomist)}>Edit</Button>
              {phlebotomist.archived ? (
                <Button size="sm" colorScheme="green" variant="outline" onClick={() => handleArchive(phlebotomist.id, true)}>Restore</Button>
              ) : (
                <Button size="sm" colorScheme="red" variant="outline" onClick={() => handleArchive(phlebotomist.id, false)}>Archive</Button>
              )}
            </HStack>
          </Flex>

          {/* Stats Row */}
          <HStack spacing={2}>
            {[
              { label: 'Total', value: stats.total, color: 'blue', bg: 'blue.50', status: 'all' },
              { label: 'Pending', value: stats.pending, color: 'yellow', bg: 'yellow.50', status: 'pending' },
              { label: 'Active', value: stats.in_progress, color: 'blue', bg: 'blue.50', status: 'in_progress' },
              { label: 'Done', value: stats.completed, color: 'green', bg: 'green.50', status: 'completed' },
              { label: 'Cancelled', value: stats.cancelled, color: 'red', bg: 'red.50', status: 'cancelled' },
            ].map(s => (
              <Box
                key={s.label}
                flex={1}
                py={2}
                textAlign="center"
                borderRadius="md"
                bg={s.bg}
                cursor={s.value > 0 ? 'pointer' : 'default'}
                _hover={s.value > 0 ? { opacity: 0.8 } : {}}
                transition="all 0.15s"
                onClick={() => {
                  if (s.value > 0) navigate(`/admin/submissions?phlebotomistId=${phlebotomist.id}&status=${s.status}`);
                }}
              >
                <Text fontSize="lg" fontWeight="700" color={`${s.color}.600`} lineHeight="1">{s.value}</Text>
                <Text fontSize="2xs" color="gray.500" fontWeight="500" mt={1}>{s.label}</Text>
              </Box>
            ))}
          </HStack>
        </Box>

        {/* Readiness — two checkpoints between onboarding done and being assignable on the route planner */}
        <ReadinessPanel phlebotomist={phlebotomist} />

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {/* Contact */}
          <SectionCard title="Contact Information" colorScheme="blue">
            <SimpleGrid columns={2} spacing={3}>
              <DetailField label="Name" value={phlebotomist.full_name} />
              <DetailField label="Email" value={phlebotomist.email} />
              <DetailField label="Phone" value={phlebotomist.phone} />
              <DetailField label="Address" value={phlebotomist.company_address} />
            </SimpleGrid>
          </SectionCard>

          {/* Service */}
          <SectionCard title="Service Details" colorScheme="purple">
            <SimpleGrid columns={2} spacing={3}>
              <DetailField label="Reference Lab" value={getLabName(phlebotomist.lab_id)} />
              <DetailField label="Patient Fee Range" value={`$${phlebotomist.min_draw_fee || '0.00'} - $${phlebotomist.max_draw_fee || '0.00'}`} />
            </SimpleGrid>
          </SectionCard>
        </SimpleGrid>

        {/* Compensation */}
        <SectionCard title="Agreement Compensation" colorScheme="blue">
          <Flex gap={6} align="flex-start" flexWrap="wrap">
            <Box>
              <Text fontSize="2xs" color="gray.400" fontWeight="500" mb={0.5}>Per Draw</Text>
              <Text fontSize="2xl" fontWeight="700" color="blue.600">
                ${phlebotomist.lab_draw_fee !== null && phlebotomist.lab_draw_fee !== undefined ? parseFloat(phlebotomist.lab_draw_fee).toFixed(2) : '0.00'}
              </Text>
            </Box>
            <Box>
              <Text fontSize="2xs" color="gray.400" fontWeight="500" mb={0.5}>Payment Terms</Text>
              <Text fontSize="sm" color="gray.700">Biweekly invoice, paid within 15 days</Text>
              <Text fontSize="xs" color="gray.400">$10 late fee applies</Text>
            </Box>
            <Button size="xs" variant="outline" colorScheme="blue" onClick={() => handleEdit(phlebotomist)}>Edit</Button>
          </Flex>
        </SectionCard>

        {/* ACH Payment */}
        {paymentInfo && (
          <SectionCard title="ACH Payment Information" colorScheme="green">
            <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
              <DetailField label="Account Name" value={paymentInfo.bank_account_name} />
              <DetailField label="Bank Name" value={paymentInfo.bank_name} />
              <DetailField label="Routing Number" value={paymentInfo.bank_routing_number} mono />
              <Box>
                <HStack spacing={1} mb={0.5}>
                  <Text fontSize="2xs" color="gray.400" fontWeight="500">Account Number</Text>
                  {paymentInfo.bank_account_number && (
                    <IconButton
                      size="xs"
                      icon={showAccountNumber ? <ViewOffIcon /> : <ViewIcon />}
                      onClick={() => setShowAccountNumber(!showAccountNumber)}
                      aria-label={showAccountNumber ? 'Hide' : 'Show'}
                      variant="ghost"
                      minW="auto"
                    />
                  )}
                </HStack>
                <Text fontSize="sm" fontWeight="500" fontFamily="mono" color="gray.800">
                  {paymentInfo.bank_account_number
                    ? (showAccountNumber ? paymentInfo.bank_account_number : `****${paymentInfo.bank_account_number.slice(-4)}`)
                    : '---'}
                </Text>
              </Box>
              <DetailField label="Account Type" value={paymentInfo.bank_account_type} />
              <DetailField label="Payment Frequency" value={paymentInfo.payment_frequency} />
            </SimpleGrid>
          </SectionCard>
        )}

        {/* Service Areas */}
        <SectionCard title="Service Areas" colorScheme="teal">
          {phlebotomist.service_areas?.length > 0 ? (
            <Wrap spacing={2}>
              {phlebotomist.service_areas.map((area, index) => (
                <WrapItem key={index}>
                  <Badge variant="subtle" colorScheme="gray" px={2} py={1} borderRadius="md" fontSize="xs">
                    {area.zip_code} ({area.radius} mi)
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          ) : (
            <Text fontSize="sm" color="gray.400">No service areas configured</Text>
          )}
        </SectionCard>

        {/* NYC service ZIPs picked during onboarding */}
        {Array.isArray(phlebotomist.service_zips) && phlebotomist.service_zips.length > 0 && (
          <SectionCard title={`NYC Service ZIPs (${phlebotomist.service_zips.length})`} colorScheme="teal">
            <Wrap spacing={2}>
              {phlebotomist.service_zips.map((zip) => (
                <WrapItem key={zip}>
                  <Badge variant="subtle" colorScheme="teal" px={2} py={1} borderRadius="md" fontSize="xs" fontFamily="mono">
                    {zip}
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          </SectionCard>
        )}

        {/* Weekly availability from onboarding */}
        {sortedHours.length > 0 && (
          <SectionCard title="Weekly Availability" colorScheme="cyan">
            <SimpleGrid columns={{ base: 2, md: 7 }} spacing={2}>
              {sortedHours.map((slot, i) => (
                <Box key={`${slot.day_of_week}-${i}`} p={2} bg="green.50" borderRadius="md" textAlign="center">
                  <Text fontSize="2xs" textTransform="uppercase" fontWeight="700" color="green.600" mb={1}>
                    {dayLabel(slot.day_of_week)}
                  </Text>
                  <Text fontSize="xs" fontFamily="mono" color="green.700">
                    {fmtTime(slot.start_time)}<br />{fmtTime(slot.end_time)}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </SectionCard>
        )}

        {/* W-9 tax info collected during onboarding */}
        {phlebotomist.w9_signed_at && (
          <SectionCard title="W-9 Tax Information" colorScheme="orange">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={3}>
              <DetailField label="Legal Name" value={phlebotomist.w9_data?.legal_name} />
              <DetailField label="Business Name" value={phlebotomist.w9_data?.business_name} />
              <DetailField label="Tax Classification" value={phlebotomist.w9_data?.tax_classification} />
              <DetailField
                label={`TIN (${(phlebotomist.w9_data?.tin_type || 'ssn').toUpperCase()})`}
                value={phlebotomist.w9_data?.tin ? `••• ${String(phlebotomist.w9_data.tin).slice(-4)}` : null}
                mono
              />
              <DetailField
                label="Address"
                value={[phlebotomist.w9_data?.address, phlebotomist.w9_data?.address2].filter(Boolean).join(', ')}
              />
              <DetailField
                label="City / State / ZIP"
                value={[
                  phlebotomist.w9_data?.city,
                  [phlebotomist.w9_data?.state, phlebotomist.w9_data?.zip].filter(Boolean).join(' '),
                ].filter(Boolean).join(', ')}
              />
              <DetailField label="Signed" value={new Date(phlebotomist.w9_signed_at).toLocaleString()} />
            </SimpleGrid>
            {phlebotomist.w9_pdf_path && (
              <Button size="xs" variant="outline" colorScheme="orange" onClick={() => openCredential(phlebotomist.w9_pdf_path)}>
                View signed W-9 PDF
              </Button>
            )}
          </SectionCard>
        )}

        {/* Credential uploads */}
        {(phlebotomist.drivers_license_path || phlebotomist.phlebotomy_cert_path) && (
          <SectionCard title="Credentials" colorScheme="red">
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <CredentialThumb
                label="Driver's License"
                path={phlebotomist.drivers_license_path}
                filename={phlebotomist.drivers_license_filename}
                uploadedAt={phlebotomist.drivers_license_uploaded_at}
              />
              <CredentialThumb
                label="Phlebotomy Certification"
                path={phlebotomist.phlebotomy_cert_path}
                filename={phlebotomist.phlebotomy_cert_filename}
                uploadedAt={phlebotomist.phlebotomy_cert_uploaded_at}
              />
            </SimpleGrid>
          </SectionCard>
        )}

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          {/* Admin & Portal */}
          <SectionCard title="Admin Assignment" colorScheme="purple">
            <VStack align="stretch" spacing={2}>
              <DetailField label="Current Admin" value={getAdminName(phlebotomist.assigned_admin_id)} />
              <Select
                placeholder="Assign to admin..."
                size="sm"
                onChange={(e) => handleAssignAdmin(phlebotomist.id, e.target.value || null)}
                value={phlebotomist.assigned_admin_id || ''}
                bg="gray.50"
              >
                <option value="">Unassign</option>
                {adminUsers.map(admin => (
                  <option key={admin.id} value={admin.id}>{admin.name}</option>
                ))}
              </Select>
            </VStack>
          </SectionCard>

          <SectionCard title="Portal Access" colorScheme="cyan">
            <VStack align="stretch" spacing={2}>
              {/* Onboarding link + status */}
              {phlebotomist.onboarding_token && (
                <Box>
                  <HStack mb={1} spacing={2} flexWrap="wrap">
                    <Text fontSize="2xs" fontWeight="700" color="gray.500" textTransform="uppercase">Onboarding</Text>
                    <Badge
                      fontSize="2xs"
                      colorScheme={
                        phlebotomist.onboarding_status === 'completed' ? 'green'
                          : phlebotomist.onboarding_status === 'in_progress' ? 'yellow'
                          : 'gray'
                      }
                      borderRadius="full"
                    >
                      {phlebotomist.onboarding_status === 'completed' ? 'Completed'
                        : phlebotomist.onboarding_status === 'in_progress' ? 'In progress'
                        : 'Pending'}
                    </Badge>
                    <Badge
                      fontSize="2xs"
                      colorScheme={phlebotomist.password_set_at ? 'green' : 'gray'}
                      borderRadius="full"
                      title={phlebotomist.password_set_at ? `Password set ${new Date(phlebotomist.password_set_at).toLocaleString()}` : 'Phleb has not set a sign-in password yet'}
                    >
                      {phlebotomist.password_set_at ? 'Password set' : 'No password'}
                    </Badge>
                    {phlebotomist.email && (
                      <>
                        <Button
                          size="2xs"
                          variant="link"
                          colorScheme="blue"
                          fontSize="2xs"
                          onClick={async () => {
                            try {
                              const resp = await fetch('/api/send-phleb-password-reset', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ email: phlebotomist.email }),
                              });
                              const body = await resp.json().catch(() => ({}));
                              if (resp.status === 429) {
                                toast({ title: 'Rate limited by Supabase', description: body.error || 'One email per 60 seconds. Try again shortly.', status: 'warning', duration: 6000, isClosable: true });
                                return;
                              }
                              if (!resp.ok) throw new Error(body.error || `HTTP ${resp.status}`);
                              toast({ title: 'Password setup email sent', description: phlebotomist.email, status: 'success', duration: 4000, isClosable: true });
                            } catch (e) {
                              toast({ title: 'Could not send email', description: e.message, status: 'error', duration: 5000, isClosable: true });
                            }
                          }}
                        >
                          Send setup email
                        </Button>
                        <Button
                          size="2xs"
                          variant="link"
                          colorScheme="purple"
                          fontSize="2xs"
                          onClick={async () => {
                            try {
                              const resp = await fetch('/api/generate-phleb-setup-link', {
                                method: 'POST',
                                headers: { 'Content-Type': 'application/json' },
                                body: JSON.stringify({ email: phlebotomist.email }),
                              });
                              const body = await resp.json().catch(() => ({}));
                              if (!resp.ok) throw new Error(body.error || `HTTP ${resp.status}`);
                              setSetupLink({ url: body.url, email: body.email });
                            } catch (e) {
                              toast({ title: 'Could not generate link', description: e.message, status: 'error', duration: 5000, isClosable: true });
                            }
                          }}
                        >
                          Copy setup link
                        </Button>
                      </>
                    )}
                  </HStack>
                  <HStack>
                    <Text fontSize="xs" fontFamily="mono" bg="gray.50" px={2} py={1} borderRadius="md" flex={1} noOfLines={1}>
                      /phlebotomist/onboard/{phlebotomist.onboarding_token}
                    </Text>
                    <IconButton
                      icon={copiedId === `onb-${phlebotomist.id}` ? <CheckIcon color="green.500" /> : <CopyIcon />}
                      size="xs"
                      variant="ghost"
                      onClick={() => copyToClipboard(`${window.location.origin}/phlebotomist/onboard/${phlebotomist.onboarding_token}`, `onb-${phlebotomist.id}`)}
                      aria-label="Copy onboarding link"
                    />
                  </HStack>
                </Box>
              )}
              <HStack>
                <Text fontSize="xs" fontFamily="mono" bg="gray.50" px={2} py={1} borderRadius="md" flex={1} noOfLines={1}>
                  /lab/{phlebotomist.id}
                </Text>
                <IconButton
                  icon={copiedId === phlebotomist.id ? <CheckIcon color="green.500" /> : <CopyIcon />}
                  size="xs"
                  variant="ghost"
                  onClick={() => copyToClipboard(`${window.location.origin}/lab/${phlebotomist.id}`, phlebotomist.id)}
                  aria-label="Copy portal link"
                />
              </HStack>
              <HStack spacing={2}>
                <Button as="a" href={`/lab/${phlebotomist.id}`} target="_blank" variant="outline" size="xs" flex={1}>
                  View Portal
                </Button>
                <Button
                  size="xs"
                  colorScheme="green"
                  variant="outline"
                  flex={1}
                  onClick={() => {
                    const agreementLink = `${window.location.origin}/phlebotomist/${phlebotomist.id}/agreement`;
                    copyToClipboard(agreementLink, `agreement-${phlebotomist.id}`);
                    toast({ title: 'Agreement link copied', status: 'success', duration: 3000, isClosable: true });
                  }}
                  leftIcon={copiedId === `agreement-${phlebotomist.id}` ? <CheckIcon /> : <CopyIcon />}
                >
                  {copiedId === `agreement-${phlebotomist.id}` ? 'Copied!' : 'Agreement Link'}
                </Button>
              </HStack>
            </VStack>
          </SectionCard>
        </SimpleGrid>

        {/* Tracking */}
        {(phlebotomist.created_by_user || phlebotomist.updated_by_user) && (
          <SectionCard title="Tracking" colorScheme="gray">
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
              {phlebotomist.created_by_user && <DetailField label="Created By" value={phlebotomist.created_by_user} />}
              {phlebotomist.updated_by_user && <DetailField label="Updated By" value={phlebotomist.updated_by_user} />}
              <DetailField label="Created" value={new Date(phlebotomist.created_at).toLocaleDateString()} />
              <DetailField label="Updated" value={new Date(phlebotomist.updated_at).toLocaleDateString()} />
            </SimpleGrid>
          </SectionCard>
        )}

        {/* Agreement History */}
        <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" overflow="hidden">
          <Accordion allowToggle defaultIndex={agreementHistory.length > 0 && agreementHistory[0]?.is_current ? [0] : []}>
            <AccordionItem border="none">
              <AccordionButton px={4} py={3} _hover={{ bg: 'gray.50' }}>
                <HStack justify="space-between" w="100%">
                  <HStack spacing={2}>
                    <Text fontSize="xs" fontWeight="700" color="gray.500" textTransform="uppercase" letterSpacing="0.05em">Agreement History</Text>
                    {agreementHistory.length > 0 && (
                      <Badge fontSize="2xs" colorScheme="blue" borderRadius="full">{agreementHistory.length}</Badge>
                    )}
                  </HStack>
                  <HStack spacing={2}>
                    {loadingHistory && <Text fontSize="xs" color="gray.400">Loading...</Text>}
                    {agreementHistory.length > 0 && agreementHistory[0]?.is_current && (
                      <Badge colorScheme="blue" fontSize="2xs" borderRadius="full">
                        Current: ${agreementHistory[0]?.per_draw_fee ? parseFloat(agreementHistory[0].per_draw_fee).toFixed(2) : 'N/A'}
                      </Badge>
                    )}
                    <AccordionIcon color="gray.400" />
                  </HStack>
                </HStack>
              </AccordionButton>
              <AccordionPanel px={4} pb={3}>
                {agreementHistory.length > 0 ? (
                  <VStack align="stretch" spacing={2}>
                    {agreementHistory.map((agreement) => (
                      <Box
                        key={agreement.id}
                        p={3}
                        borderRadius="md"
                        bg={agreement.is_current ? 'blue.50' : 'gray.50'}
                        border="1px solid"
                        borderColor={agreement.is_current ? 'blue.100' : 'gray.100'}
                      >
                        <Flex justify="space-between" align="center" mb={2}>
                          <HStack spacing={2}>
                            <Badge colorScheme={agreement.is_current ? 'blue' : 'gray'} fontSize="2xs" borderRadius="full">
                              {agreement.is_current ? 'Current' : 'Previous'}
                            </Badge>
                            {agreement.agreement_version && (
                              <Badge variant="outline" fontSize="2xs">{agreement.agreement_version}</Badge>
                            )}
                            <Text fontSize="sm" fontWeight="600" color={agreement.is_current ? 'blue.700' : 'gray.600'}>
                              ${agreement.per_draw_fee ? parseFloat(agreement.per_draw_fee).toFixed(2) : 'N/A'}
                            </Text>
                          </HStack>
                          <Text fontSize="xs" color="gray.400">
                            {agreement.agreement_signed_at ? new Date(agreement.agreement_signed_at).toLocaleDateString() : 'Not signed'}
                          </Text>
                        </Flex>
                        <SimpleGrid columns={4} spacing={2}>
                          <DetailField label="Payment" value={agreement.payment_method} />
                          <DetailField label="Frequency" value={agreement.payment_frequency} />
                          <DetailField label="Effective" value={agreement.agreement_effective_date ? new Date(agreement.agreement_effective_date).toLocaleDateString() : null} />
                          {agreement.agreement_printed_name && <DetailField label="Signed By" value={agreement.agreement_printed_name} />}
                        </SimpleGrid>
                      </Box>
                    ))}
                  </VStack>
                ) : !loadingHistory ? (
                  <Text fontSize="xs" color="gray.400" textAlign="center" py={3}>No agreement history</Text>
                ) : null}
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>

        {/* Actions */}
        <HStack spacing={2}>
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={() => downloadAgreement(phlebotomist)}
          >
            Download Agreement
          </Button>
          <Button
            size="sm"
            colorScheme="green"
            variant="outline"
            onClick={() => {
              const agreementLink = `${window.location.origin}/phlebotomist/${phlebotomist.id}/agreement`;
              copyToClipboard(agreementLink, `agreement-${phlebotomist.id}`);
              toast({ title: 'Agreement link copied', status: 'success', duration: 3000, isClosable: true });
            }}
            leftIcon={copiedId === `agreement-${phlebotomist.id}` ? <CheckIcon /> : <CopyIcon />}
          >
            {copiedId === `agreement-${phlebotomist.id}` ? 'Link Copied!' : 'Copy Agreement Link'}
          </Button>
        </HStack>
      </VStack>
    );
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minH="400px">
        <Text fontSize="sm" color="gray.500">Loading phlebotomists...</Text>
      </Flex>
    );
  }

  return (
    <Box>
      <Tabs colorScheme="blue" isLazy>
        <Box bg="white" px={6} pt={4} borderBottom="1px solid" borderColor="gray.100">
          <Flex justify="space-between" align="center" mb={3}>
            <Box>
              <Text fontSize="xl" fontWeight="700" color="gray.800">Phlebotomist Management</Text>
              <Text fontSize="xs" color="gray.400" mt={0.5}>
                {filteredPhlebotomists.length} of {phlebotomists.length} labs shown
              </Text>
            </Box>
            <Button leftIcon={<AddIcon />} colorScheme="blue" size="sm" onClick={() => { resetForm(); onOpen(); }}>
              Add Phlebotomist
            </Button>
          </Flex>
          <TabList>
            <Tab fontSize="sm" fontWeight="600">
              <Icon as={FaUsers} mr={2} boxSize={3.5} />
              Phlebotomists
            </Tab>
            <Tab fontSize="sm" fontWeight="600">
              <Icon as={FaMapMarkedAlt} mr={2} boxSize={3.5} />
              Coverage Map
            </Tab>
          </TabList>
        </Box>

        <TabPanels>
          <TabPanel p={0}>
            <VStack spacing={4} align="stretch" pt={4}>
        <Box px={6}>
          {/* Search and Filters */}
          <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={4} mb={4}>
            <VStack spacing={3} align="stretch">
              <InputGroup size="sm">
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" boxSize={3} />
                </InputLeftElement>
                <Input
                  placeholder="Search by name, company, email, address, or phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  bg="gray.50"
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  _focus={{ borderColor: 'blue.400', bg: 'white' }}
                />
              </InputGroup>
              <Flex gap={4} flexWrap="wrap">
                <Box>
                  <Text fontSize="2xs" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>Status</Text>
                  <HStack spacing={1}>
                    <Button
                      size="xs"
                      variant={statusFilters.active ? "solid" : "ghost"}
                      colorScheme={statusFilters.active ? "green" : "gray"}
                      onClick={() => toggleStatusFilter('active')}
                      fontWeight="500"
                      borderRadius="full"
                    >
                      Active ({phlebotomists.filter(p => p.email && p.company_name && !p.archived).length})
                    </Button>
                    <Button
                      size="xs"
                      variant={statusFilters.inactive ? "solid" : "ghost"}
                      colorScheme={statusFilters.inactive ? "red" : "gray"}
                      onClick={() => toggleStatusFilter('inactive')}
                      fontWeight="500"
                      borderRadius="full"
                    >
                      Inactive ({phlebotomists.filter(p => (!p.email || !p.company_name) && !p.archived).length})
                    </Button>
                    <Button
                      size="xs"
                      variant={statusFilters.archived ? "solid" : "ghost"}
                      colorScheme="gray"
                      onClick={() => toggleStatusFilter('archived')}
                      fontWeight="500"
                      borderRadius="full"
                    >
                      Archived ({phlebotomists.filter(p => p.archived).length})
                    </Button>
                  </HStack>
                </Box>
                <Box borderLeft="1px solid" borderColor="gray.200" pl={4}>
                  <Text fontSize="2xs" fontWeight="600" color="gray.400" textTransform="uppercase" letterSpacing="0.05em" mb={1.5}>Assignment</Text>
                  <HStack spacing={1}>
                    <Button
                      size="xs"
                      variant={adminFilters.all ? "solid" : "ghost"}
                      colorScheme={adminFilters.all ? "purple" : "gray"}
                      onClick={() => toggleAdminFilter('all')}
                      fontWeight="500"
                      borderRadius="full"
                    >
                      All ({phlebotomists.length})
                    </Button>
                    <Button
                      size="xs"
                      variant={adminFilters.mine ? "solid" : "ghost"}
                      colorScheme={adminFilters.mine ? "purple" : "gray"}
                      onClick={() => toggleAdminFilter('mine')}
                      fontWeight="500"
                      borderRadius="full"
                    >
                      Mine ({phlebotomists.some(p => p.assigned_admin_id !== undefined) ? phlebotomists.filter(p => p.assigned_admin_id && p.assigned_admin_id === adminUser?.id).length : phlebotomists.length})
                    </Button>
                    <Button
                      size="xs"
                      variant={adminFilters.unassigned ? "solid" : "ghost"}
                      colorScheme={adminFilters.unassigned ? "purple" : "gray"}
                      onClick={() => toggleAdminFilter('unassigned')}
                      fontWeight="500"
                      borderRadius="full"
                    >
                      Unassigned ({phlebotomists.some(p => p.assigned_admin_id !== undefined) ? phlebotomists.filter(p => !p.assigned_admin_id).length : phlebotomists.length})
                    </Button>
                  </HStack>
                </Box>
              </Flex>
            </VStack>
          </Box>

          {/* Main Content Grid */}
          <Grid templateColumns={{ base: "1fr", lg: "320px 1fr" }} gap={4} minH="600px">
            {/* Left Sidebar */}
            <GridItem>
              <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" maxH="calc(100vh - 260px)" overflow="hidden" display="flex" flexDirection="column">
                <Box px={3} py={2.5} borderBottom="1px solid" borderColor="gray.100">
                  <Text fontSize="xs" fontWeight="600" color="gray.500">{filteredPhlebotomists.length} Phlebotomists</Text>
                </Box>
                <Box overflowY="auto" flex={1} p={2}>
                  <VStack spacing={1} align="stretch">
                    {filteredPhlebotomists.map((phlebotomist) => (
                      <PhlebotomistCard
                        key={phlebotomist.id}
                        phlebotomist={phlebotomist}
                        isSelected={selectedPhlebotomist?.id === phlebotomist.id}
                        onClick={() => setSelectedPhlebotomist(phlebotomist)}
                      />
                    ))}
                    {filteredPhlebotomists.length === 0 && (
                      <Text color="gray.400" textAlign="center" py={8} fontSize="sm">
                        No phlebotomists found
                      </Text>
                    )}
                  </VStack>
                </Box>
              </Box>
            </GridItem>

            {/* Right Side */}
            <GridItem>
              {selectedPhlebotomist ? (
                <PhlebotomistDetails phlebotomist={selectedPhlebotomist} />
              ) : (
                <Box bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100" p={6}>
                  <VStack spacing={5}>
                    <SimpleGrid columns={3} spacing={4} w="100%">
                      <Flex align="center" gap={3} p={4} bg="blue.50" borderRadius="lg">
                        <Icon as={FaUsers} color="blue.500" boxSize={5} />
                        <Box>
                          <Text fontSize="2xs" color="gray.400" fontWeight="500">Labs Shown</Text>
                          <Text fontWeight="700" fontSize="xl" color="gray.800">{filteredPhlebotomists.length}</Text>
                        </Box>
                      </Flex>
                      <Flex align="center" gap={3} p={4} bg="purple.50" borderRadius="lg">
                        <Icon as={FaFileMedical} color="purple.500" boxSize={5} />
                        <Box>
                          <Text fontSize="2xs" color="gray.400" fontWeight="500">Total Submissions</Text>
                          <Text fontWeight="700" fontSize="xl" color="gray.800">{aggregateStats.total}</Text>
                        </Box>
                      </Flex>
                      <Flex align="center" gap={3} p={4} bg="green.50" borderRadius="lg">
                        <Icon as={FaCheckCircle} color="green.500" boxSize={5} />
                        <Box>
                          <Text fontSize="2xs" color="gray.400" fontWeight="500">Completed</Text>
                          <Text fontWeight="700" fontSize="xl" color="gray.800">{aggregateStats.completed}</Text>
                        </Box>
                      </Flex>
                    </SimpleGrid>
                    <HStack spacing={2} flexWrap="wrap">
                      {aggregateStats.pending > 0 && <Badge variant="subtle" colorScheme="yellow" borderRadius="full" px={2}>{aggregateStats.pending} Pending</Badge>}
                      {aggregateStats.inProgress > 0 && <Badge variant="subtle" colorScheme="blue" borderRadius="full" px={2}>{aggregateStats.inProgress} In progress</Badge>}
                      {aggregateStats.waiting > 0 && <Badge variant="subtle" colorScheme="orange" borderRadius="full" px={2}>{aggregateStats.waiting} Waiting</Badge>}
                      {aggregateStats.cancelled > 0 && <Badge variant="subtle" colorScheme="red" borderRadius="full" px={2}>{aggregateStats.cancelled} Cancelled</Badge>}
                      {aggregateStats.total === 0 && <Text fontSize="sm" color="gray.400">No submissions in selected labs</Text>}
                    </HStack>
                    <Box pt={4} borderTopWidth="1px" borderColor="gray.100" textAlign="center" w="100%">
                      <Text fontSize="sm" color="gray.400" mb={3}>Select a phlebotomist from the list to view details</Text>
                      <Button size="sm" leftIcon={<AddIcon />} colorScheme="blue" variant="outline" onClick={() => { resetForm(); onOpen(); }}>
                        Add New Phlebotomist
                      </Button>
                    </Box>
                  </VStack>
                </Box>
              )}
            </GridItem>
          </Grid>
        </Box>
            </VStack>
          </TabPanel>

          <TabPanel p={6}>
            <PhlebCoverageMap />
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size={selectedPhlebotomist ? "xl" : "md"}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedPhlebotomist ? 'Edit Phlebotomist' : 'Add New Phlebotomist'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {!selectedPhlebotomist ? (
              // New flow: admin enters just name + email. The phleb fills the rest via the onboarding link.
              <form onSubmit={handleSubmit}>
                <VStack spacing={4} align="stretch">
                  <Alert status="info" fontSize="sm" borderRadius="md">
                    <AlertIcon />
                    After you save, we'll generate a unique onboarding link. Send it to the phleb so they can confirm details, sign a W-9 and service agreement, pick NYC service areas on a map, and set weekly availability.
                  </Alert>
                  <FormControl isRequired>
                    <FormLabel>Full Name</FormLabel>
                    <Input
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      placeholder="Jane Doe"
                    />
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="jane@example.com"
                    />
                  </FormControl>
                  <SimpleGrid columns={2} spacing={4} w="100%">
                    <FormControl isRequired>
                      <FormLabel>Lab Bill Rate ($)</FormLabel>
                      <NumberInput
                        value={formData.lab_draw_fee}
                        onChange={(v) => handleNumberChange('lab_draw_fee', v)}
                        min={0}
                        step={0.01}
                      >
                        <NumberInputField placeholder="e.g. 50.00" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        What the lab is billed per draw.
                      </Text>
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Phleb Pay Rate ($)</FormLabel>
                      <NumberInput
                        value={formData.phleb_pay_rate}
                        onChange={(v) => handleNumberChange('phleb_pay_rate', v)}
                        min={0}
                        step={0.01}
                      >
                        <NumberInputField placeholder="e.g. 35.00" />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        What the phleb earns per draw. Locked into their agreement at onboarding.
                      </Text>
                    </FormControl>
                  </SimpleGrid>
                </VStack>
              </form>
            ) : (
              // Edit flow: admin can still override anything the phleb filled in.
              <form onSubmit={handleSubmit}>
                <VStack spacing={4}>
                  <SimpleGrid columns={2} spacing={4} w="100%">
                    <FormControl isRequired>
                      <FormLabel>Full Name</FormLabel>
                      <Input name="full_name" value={formData.full_name} onChange={handleInputChange} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Email</FormLabel>
                      <Input name="email" type="email" value={formData.email} onChange={handleInputChange} />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Phone</FormLabel>
                      <Input name="phone" value={formData.phone} onChange={handleInputChange} />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>Company Name</FormLabel>
                      <Input name="company_name" value={formData.company_name} onChange={handleInputChange} />
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Company Address</FormLabel>
                    <Input name="company_address" value={formData.company_address} onChange={handleInputChange} />
                  </FormControl>

                  <SimpleGrid columns={3} spacing={4} w="100%">
                    <FormControl>
                      <FormLabel>Min Draw Fee ($)</FormLabel>
                      <NumberInput value={formData.min_draw_fee} onChange={(v) => handleNumberChange('min_draw_fee', v)} min={0} step={0.01}>
                        <NumberInputField />
                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Max Draw Fee ($)</FormLabel>
                      <NumberInput value={formData.max_draw_fee} onChange={(v) => handleNumberChange('max_draw_fee', v)} min={0} step={0.01}>
                        <NumberInputField />
                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Lab Bill Rate ($)</FormLabel>
                      <NumberInput value={formData.lab_draw_fee} onChange={(v) => handleNumberChange('lab_draw_fee', v)} min={0} step={0.01}>
                        <NumberInputField />
                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Amount we bill the lab per completed draw (drives weekly lab invoices).
                      </Text>
                    </FormControl>
                  </SimpleGrid>

                  <SimpleGrid columns={2} spacing={4} mt={2}>
                    <FormControl>
                      <FormLabel>Phleb Pay Rate ($)</FormLabel>
                      <NumberInput value={formData.phleb_pay_rate} onChange={(v) => handleNumberChange('phleb_pay_rate', v)} min={0} step={0.01}>
                        <NumberInputField />
                        <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                      </NumberInput>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Amount the phleb earns per completed draw (drives weekly payslips). Shown in their onboarding agreement.
                      </Text>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Margin</FormLabel>
                      {(() => {
                        const lab = parseFloat(formData.lab_draw_fee);
                        const pay = parseFloat(formData.phleb_pay_rate);
                        if (isNaN(lab) || isNaN(pay)) {
                          return <Text fontSize="sm" color="gray.500" mt={2}>Set both rates to preview margin.</Text>;
                        }
                        const margin = lab - pay;
                        const pct = lab > 0 ? (margin / lab) * 100 : 0;
                        const tone = margin < 0 ? 'red.500' : margin === 0 ? 'gray.600' : 'teal.600';
                        return (
                          <Box mt={2}>
                            <Text fontSize="xl" fontWeight="bold" color={tone}>
                              ${margin.toFixed(2)}
                              <Text as="span" fontSize="sm" color="gray.500" fontWeight="normal" ml={2}>
                                ({pct.toFixed(0)}%)
                              </Text>
                            </Text>
                            <Text fontSize="xs" color="gray.500">
                              {margin < 0 ? 'Negative — phleb is paid more than lab is billed.' : 'Per draw, kept by AtHomeBloodDraw.'}
                            </Text>
                          </Box>
                        );
                      })()}
                    </FormControl>
                  </SimpleGrid>

                  <FormControl>
                    <FormLabel>Service Areas (legacy ZIP/radius)</FormLabel>
                    <VStack spacing={2} align="stretch">
                      {formData.service_areas.map((area, index) => (
                        <HStack key={index} spacing={2}>
                          <Input placeholder="ZIP Code" value={area.zip_code} onChange={(e) => updateServiceArea(index, 'zip_code', e.target.value)} />
                          <NumberInput value={area.radius} onChange={(v) => updateServiceArea(index, 'radius', parseInt(v))} min={1} max={100} w="120px">
                            <NumberInputField />
                            <NumberInputStepper><NumberIncrementStepper /><NumberDecrementStepper /></NumberInputStepper>
                          </NumberInput>
                          <Text fontSize="sm" color="gray.500" w="60px">miles</Text>
                          <IconButton icon={<DeleteIcon />} size="sm" colorScheme="red" onClick={() => removeServiceArea(index)} isDisabled={formData.service_areas.length === 1} />
                        </HStack>
                      ))}
                      <Button size="sm" variant="outline" onClick={addServiceArea}>Add Service Area</Button>
                    </VStack>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      New phlebs pick ZIPs on a NYC map during onboarding (stored in <code>service_zips</code>).
                    </Text>
                  </FormControl>
                </VStack>
              </form>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading}>
              {selectedPhlebotomist ? 'Update Phlebotomist' : 'Create & Generate Link'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Setup link generated for an existing phleb */}
      <Modal isOpen={!!setupLink} onClose={() => setSetupLink(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Password Setup Link</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="gray.600">
                One-time link for <b>{setupLink?.email}</b>. Share it directly — clicking it will let them set a password and sign in. No email is sent.
              </Text>
              <HStack>
                <Input value={setupLink?.url || ''} isReadOnly fontSize="xs" fontFamily="mono" />
                <Button
                  leftIcon={copiedId === 'setup-link' ? <CheckIcon /> : <CopyIcon />}
                  colorScheme="purple"
                  onClick={() => copyToClipboard(setupLink.url, 'setup-link')}
                >
                  {copiedId === 'setup-link' ? 'Copied' : 'Copy'}
                </Button>
              </HStack>
              <Alert status="info" fontSize="xs" borderRadius="md">
                <AlertIcon />
                Treat this link like a temporary password. Anyone who opens it can set the phleb's account password.
              </Alert>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setSetupLink(null)}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Newly-created phleb: show onboarding link */}
      <Modal isOpen={!!newOnboardingLink} onClose={() => setNewOnboardingLink(null)} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Onboarding Link Ready</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={3} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Send this link to <b>{newOnboardingLink?.name}</b>. They'll confirm their details, sign a W-9 and service agreement, choose NYC service areas on a map, and set their weekly availability.
              </Text>
              <HStack>
                <Input value={newOnboardingLink?.url || ''} isReadOnly fontSize="sm" fontFamily="mono" />
                <Button
                  leftIcon={copiedId === 'new-onboarding' ? <CheckIcon /> : <CopyIcon />}
                  colorScheme="blue"
                  onClick={() => copyToClipboard(newOnboardingLink.url, 'new-onboarding')}
                >
                  {copiedId === 'new-onboarding' ? 'Copied' : 'Copy'}
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setNewOnboardingLink(null)}>Done</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default PhlebotomistManagement; 